/**
 * FolderTree Component
 *
 * Recursive tree component for rendering folder hierarchy.
 * Supports expand/collapse, selection, and keyboard navigation.
 *
 * @module features/dashboard/components/FolderTree
 * @see SPEC-016 Requirement 3.6
 */

import React, { useCallback } from 'react';
import { cn } from '../../../lib/cn';
import type { FolderNode } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

export interface FolderTreeProps {
  /** Folder nodes to render */
  nodes: FolderNode[];
  /** Currently selected folder ID */
  selectedFolderId: string | null;
  /** Folder selection handler */
  onFolderSelect: (folderId: string | null) => void;
  /** Folder expansion toggle handler */
  onFolderToggle: (folderId: string) => void;
  /** Folder context menu handler */
  onFolderContextMenu?: (folder: FolderNode, event: React.MouseEvent) => void;
  /** Current nesting level (for indentation) */
  level?: number;
  /** Additional class names */
  className?: string;
}

export interface FolderTreeItemProps {
  /** Folder node data */
  node: FolderNode;
  /** Whether this folder is selected */
  isSelected: boolean;
  /** Folder selection handler */
  onSelect: (folderId: string) => void;
  /** Folder expansion toggle handler */
  onToggle: (folderId: string) => void;
  /** Folder context menu handler */
  onContextMenu?: (folder: FolderNode, event: React.MouseEvent) => void;
  /** Current nesting level */
  level: number;
  /** Child nodes renderer */
  children?: React.ReactNode;
}

// ============================================================================
// Icons
// ============================================================================

const ChevronIcon: React.FC<{ expanded: boolean; className?: string }> = ({
  expanded,
  className,
}) => (
  <svg
    className={cn('w-4 h-4 transition-transform duration-150', expanded && 'rotate-90', className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const FolderIcon: React.FC<{ open: boolean; className?: string }> = ({ open, className }) => (
  <svg className={cn('w-5 h-5', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {open ? (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"
      />
    ) : (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    )}
  </svg>
);

// ============================================================================
// FolderTreeItem Component
// ============================================================================

/**
 * Individual folder tree item with expand/collapse functionality
 */
const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  node,
  isSelected,
  onSelect,
  onToggle,
  onContextMenu,
  level,
  children,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = node.isExpanded ?? false;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onSelect(node.folderId);
    },
    [node.folderId, onSelect]
  );

  const handleToggleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onToggle(node.folderId);
    },
    [node.folderId, onToggle]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onContextMenu?.(node, event);
    },
    [node, onContextMenu]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelect(node.folderId);
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (hasChildren && !isExpanded) {
            onToggle(node.folderId);
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (hasChildren && isExpanded) {
            onToggle(node.folderId);
          }
          break;
      }
    },
    [node.folderId, hasChildren, isExpanded, onSelect, onToggle]
  );

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      <div
        className={cn(
          'flex items-center gap-1 px-2 py-1.5 rounded-linkode',
          'cursor-pointer select-none',
          'transition-colors duration-150',
          isSelected
            ? 'bg-linkode-bg-orange text-linkode-text'
            : 'hover:bg-linkode-bg-light text-linkode-text/80 hover:text-linkode-text'
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-selected={isSelected}
      >
        {/* Expand/Collapse Toggle */}
        <button
          type="button"
          className={cn(
            'p-0.5 rounded hover:bg-linkode-text/10',
            'focus:outline-none focus:ring-1 focus:ring-linkode-teal',
            !hasChildren && 'invisible'
          )}
          onClick={handleToggleClick}
          aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          tabIndex={-1}
        >
          <ChevronIcon expanded={isExpanded} className="text-linkode-text/50" />
        </button>

        {/* Folder Icon */}
        <FolderIcon
          open={isExpanded && hasChildren}
          className={cn(
            'flex-shrink-0',
            isSelected ? 'text-linkode-primary' : 'text-linkode-text/60'
          )}
        />

        {/* Folder Name */}
        <span className="font-mono text-sm truncate flex-1" title={node.name}>
          {node.name}
        </span>

        {/* Document Count Badge */}
        {node.docCount !== undefined && node.docCount > 0 && (
          <span className="text-xs text-linkode-text/50 font-mono px-1.5 py-0.5 bg-linkode-bg-light rounded">
            {node.docCount}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && children}
    </li>
  );
};

// ============================================================================
// FolderTree Component
// ============================================================================

/**
 * FolderTree Component
 *
 * Recursive tree component for rendering folder hierarchy.
 *
 * Features:
 * - Recursive rendering of nested folders
 * - Expand/collapse with chevron icons
 * - Visual selection state
 * - Keyboard navigation (Enter to select, Arrow keys to expand/collapse)
 * - Context menu support
 * - Document count badges
 *
 * @example
 * ```tsx
 * <FolderTree
 *   nodes={folderTree}
 *   selectedFolderId={selectedId}
 *   onFolderSelect={(id) => selectFolder(id)}
 *   onFolderToggle={(id) => toggleExpansion(id)}
 * />
 * ```
 *
 * @see Requirement 3.6
 */
export const FolderTree: React.FC<FolderTreeProps> = ({
  nodes,
  selectedFolderId,
  onFolderSelect,
  onFolderToggle,
  onFolderContextMenu,
  level = 0,
  className,
}) => {
  if (!nodes || nodes.length === 0) {
    return null;
  }

  return (
    <ul
      className={cn('list-none m-0 p-0', className)}
      role={level === 0 ? 'tree' : 'group'}
      aria-label={level === 0 ? 'Folder tree' : undefined}
    >
      {nodes.map((node) => (
        <FolderTreeItem
          key={node.folderId}
          node={node}
          isSelected={selectedFolderId === node.folderId}
          onSelect={onFolderSelect}
          onToggle={onFolderToggle}
          onContextMenu={onFolderContextMenu}
          level={level}
        >
          {/* Recursively render children */}
          {node.children && node.children.length > 0 && (
            <FolderTree
              nodes={node.children}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              onFolderToggle={onFolderToggle}
              onFolderContextMenu={onFolderContextMenu}
              level={level + 1}
            />
          )}
        </FolderTreeItem>
      ))}
    </ul>
  );
};

export default FolderTree;
